import { useEffect, useState } from "react";
import { PayPalScriptProvider, PayPalButtons } from "@paypal/react-paypal-js";
import { toast } from "sonner";
import api from "@/lib/api";
import { formatApiError } from "@/context/AuthContext";

/**
 * PayPalSubscribeButton
 * ----------------------
 * Renders the PayPal JS SDK subscription button using our sandbox/live plan.
 * On approval:
 *   1. Reports the subscription_id to /api/paypal/subscription
 *   2. Backend verifies with PayPal + auto-grants 30 days on the user's account
 *   3. Fires onGranted() so the parent can refresh /api/auth/me and flip the UI
 *
 * Fetches plan_id + client_id from /api/paypal/plan (which lazily creates the
 * plan in PayPal on first call). If PayPal isn't configured on the backend
 * we render a helpful fallback for the site owner.
 */
export default function PayPalSubscribeButton({ onGranted }) {
    const [plan, setPlan] = useState(null);
    const [loadError, setLoadError] = useState("");
    const [approving, setApproving] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const r = await api.get("/paypal/plan");
                if (!cancelled) setPlan(r.data);
            } catch (err) {
                if (!cancelled) setLoadError(formatApiError(err) || "PayPal is not configured yet.");
            }
        })();
        return () => { cancelled = true; };
    }, []);

    if (loadError) {
        return (
            <div className="text-sm text-[var(--sb-text-muted)] border border-[var(--sb-border)] p-4 text-center" data-testid="paypal-load-error">
                {loadError}
            </div>
        );
    }

    if (!plan) {
        return (
            <div className="text-xs text-[var(--sb-text-muted)] text-center py-4" data-testid="paypal-loading">
                Preparing secure PayPal checkout…
            </div>
        );
    }

    return (
        <div data-testid="paypal-subscribe-button-wrap">
            <PayPalScriptProvider
                options={{
                    "client-id": plan.client_id,
                    vault: true,
                    intent: "subscription",
                    currency: plan.currency || "GBP",
                    // Sandbox flag: PayPal auto-detects from the client_id, but we
                    // set the buyer country so the sandbox button shows GBP nicely.
                    "buyer-country": "GB",
                }}
            >
                <PayPalButtons
                    style={{
                        shape: "rect",
                        color: "blue",
                        layout: "vertical",
                        label: "subscribe",
                    }}
                    disabled={approving}
                    createSubscription={(data, actions) => {
                        return actions.subscription.create({ plan_id: plan.plan_id });
                    }}
                    onApprove={async (data) => {
                        setApproving(true);
                        try {
                            const r = await api.post("/paypal/subscription", {
                                subscription_id: data.subscriptionID,
                            });
                            toast.success("Subscription active — 30 days unlocked. Thank you!");
                            if (onGranted) onGranted(r.data);
                        } catch (err) {
                            toast.error(formatApiError(err) || "Payment approved but activation failed. Contact hello@dtscotslad.com.");
                        } finally {
                            setApproving(false);
                        }
                    }}
                    onError={(err) => {
                        console.error("PayPal error", err);
                        toast.error("PayPal encountered an error. Please try again.");
                    }}
                    onCancel={() => {
                        toast.info("Subscription cancelled — no charge was made.");
                    }}
                />
            </PayPalScriptProvider>
            <p className="text-[10px] text-center text-[var(--sb-text-muted)] mt-2 tracking-[0.2em] uppercase">
                {plan.env === "sandbox" ? "PayPal Sandbox · Test mode" : "Secure PayPal Checkout"}
            </p>
        </div>
    );
}
